(() => {
  if (customElements.get('hh-discount-price')) return;
  const configNode = document.getElementById('hh-discount-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }

  // Standalone Storefront quotes never receive the visitor's cookies, cart ID or
  // customer details. They do not add anything to the actual Ajax cart.
  const query = `mutation HHDiscountPrice($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        discountCodes { code applicable }
        lines(first: 2) { nodes {
          quantity
          merchandise { ... on ProductVariant { id } }
          cost { subtotalAmount { amount currencyCode } }
          discountAllocations(lineLevelOnly: false) {
            discountedAmount { amount currencyCode }
            ... on CartCodeDiscountAllocation { code }
          }
        } }
      }
      userErrors { field message }
      warnings { code message }
    }
  }`;
  const instances = new Set();
  const cache = new Map();
  const waiting = [];
  const CACHE_TTL = 60000;
  const CACHE_LIMIT = 80;
  let running = 0;
  const normalize = value => String(value || '').trim().toUpperCase();
  const codesFor = state => [...new Set((state?.codes || []).map(normalize).filter(Boolean))];
  const money = (cents, currency) => new Intl.NumberFormat(config.locale || 'de', {
    style: 'currency', currency
  }).format(cents / 100);
  const amountInCents = amount => Math.round(Number(amount) * 100);
  const text = (state, code) => String(config.texts?.[`price_${state}`] || '').replaceAll('[[code]]', code);
  const quantitySelector = 'input[name="quantity"], select[name="quantity"], clever-select[name="quantity"], clever-select#quantity';

  function drain() {
    while (running < 3 && waiting.length) {
      const job = waiting.shift();
      running += 1;
      Promise.resolve().then(job.task).then(job.resolve, job.reject).finally(() => {
        running -= 1;
        drain();
      });
    }
  }
  function schedule(task) {
    return new Promise((resolve, reject) => { waiting.push({ task, resolve, reject }); drain(); });
  }
  async function requestQuote(spec) {
    const line = { merchandiseId: `gid://shopify/ProductVariant/${spec.variant}`, quantity: spec.quantity };
    if (spec.plan) line.sellingPlanId = `gid://shopify/SellingPlan/${spec.plan}`;
    const input = { lines: [line], discountCodes: spec.codes, buyerIdentity: { countryCode: spec.country } };
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(config.endpoint || '/api/2026-10/graphql.json', {
        method: 'POST', credentials: 'omit', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query, variables: { input } }), signal: controller.signal
      });
      if (!response.ok) throw new Error('Price quote unavailable');
      const payload = await response.json();
      const result = payload.data?.cartCreate;
      // A code that excludes this purchase option is a verified non-discounted
      // quote. Other warnings (for example changed quantities) remain errors.
      const blockingWarnings = result?.warnings?.some(warning => warning.code !== 'DISCOUNT_NO_ENTITLED_LINE_ITEMS');
      if (payload.errors?.length || !result?.cart || result.userErrors?.length || blockingWarnings) {
        throw new Error('Price quote could not be verified');
      }
      const lines = result.cart.lines?.nodes;
      if (lines?.length !== 1 || lines[0].quantity !== spec.quantity || lines[0].merchandise?.id !== line.merchandiseId) {
        throw new Error('Price quote does not match the selected item');
      }
      const quoted = lines[0];
      const subtotal = quoted.cost?.subtotalAmount;
      const base = amountInCents(subtotal?.amount);
      if (subtotal?.currencyCode !== spec.currency || !Number.isFinite(base) || base !== spec.base * spec.quantity) {
        throw new Error('Price quote does not match the displayed market price');
      }
      let discount = 0;
      const allocatedCodes = new Set();
      for (const allocation of quoted.discountAllocations || []) {
        const amount = allocation.discountedAmount;
        const cents = amountInCents(amount?.amount);
        if (amount?.currencyCode !== spec.currency || !Number.isFinite(cents) || cents < 0) {
          throw new Error('Price quote discount could not be verified');
        }
        discount += cents;
        if (allocation.code && cents > 0) allocatedCodes.add(normalize(allocation.code));
      }
      if (discount > base) throw new Error('Price quote discount exceeds the item price');
      const applied = new Set((result.cart.discountCodes || [])
        .filter(code => code.applicable && allocatedCodes.has(normalize(code.code)))
        .map(code => normalize(code.code)));
      return { total: base - discount, applied };
    } finally { clearTimeout(timeout); }
  }
  function quote(spec) {
    const key = JSON.stringify(spec);
    const saved = cache.get(key);
    if (saved && saved.expires > Date.now()) return saved.promise;
    const entry = { expires: Date.now() + CACHE_TTL };
    entry.promise = schedule(() => requestQuote(spec)).catch(error => {
      if (cache.get(key) === entry) cache.delete(key);
      throw error;
    });
    cache.set(key, entry);
    while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
    return entry.promise;
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.ready = true;
      observer.unobserve(entry.target);
      entry.target.refresh();
    });
  }, { rootMargin: '150px' }) : null;

  class HHDiscountPrice extends HTMLElement {
    connectedCallback() {
      instances.add(this);
      this.revision = (this.revision || 0) + 1;
      queueMicrotask(() => {
        if (!this.isConnected) return;
        if (!this.original) {
          this.original = [...this.querySelectorAll('[data-hh-price-value], [data-hh-price-compare], [data-hh-unit-value], [data-hh-serving-value]')]
            .map(node => ({ node, html: node.innerHTML, hidden: node.hidden, className: node.getAttribute('class') }));
        }
        this.ready = this.ready || this.dataset.scope !== 'card' || !observer;
        if (!this.ready) observer.observe(this);
        this.refresh();
      });
    }
    disconnectedCallback() {
      instances.delete(this);
      observer?.unobserve(this);
      this.revision += 1;
    }
    static get observedAttributes() { return ['data-variant-id', 'data-base-price', 'data-compare-price', 'data-selling-plan', 'data-currency']; }
    attributeChangedCallback() {
      if (this.original && this.isConnected) this.refresh();
    }
    restore() {
      (this.original || []).forEach(({ node, html, hidden, className }) => {
        node.innerHTML = html;
        node.hidden = hidden;
        if (className === null) node.removeAttribute('class');
        else node.setAttribute('class', className);
      });
      delete this.dataset.discountedPrice;
      delete this.dataset.discountedLinePrice;
    }
    badges(defaultState, applied = new Set()) {
      this.dataset.discountPriceState = defaultState;
      this.querySelectorAll('[data-discount-state]').forEach(tag => {
        const code = normalize(tag.querySelector('.hh-discount-tag__label')?.textContent);
        const state = applied.has(code) ? 'applied' : defaultState;
        tag.dataset.discountState = state;
        tag.title = text(state, code);
      });
    }
    async refresh() {
      const revision = this.revision = (this.revision || 0) + 1;
      if (!this.original || !this.isConnected) return;
      const state = window.hhDiscountState;
      const codes = codesFor(state);
      if (!codes.length || state?.failed || state?.initializing || state?.busy) {
        this.restore();
        this.badges('unverified');
        return;
      }
      if (!this.ready) return;
      const quantityInput = this.dataset.scope === 'product'
        ? this.closest('.shopify-section')?.querySelector(quantitySelector) : null;
      const quantity = quantityInput ? Number(quantityInput.value ?? quantityInput.getAttribute('value') ??
        quantityInput.querySelector('option[selected]')?.value ?? 1) : 1;
      const spec = {
        codes, variant: this.dataset.variantId, plan: this.dataset.sellingPlan || '',
        quantity, country: config.country, currency: this.dataset.currency || config.currency,
        base: Number(this.dataset.basePrice)
      };
      if (!/^\d+$/.test(spec.variant || '') || (spec.plan && !/^\d+$/.test(spec.plan)) ||
          !Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(spec.base) || spec.base < 0 ||
          !/^[A-Z]{2}$/.test(spec.country || '') || !/^[A-Z]{3}$/.test(spec.currency || '')) {
        this.restore(); this.badges('unverified'); return;
      }
      const key = JSON.stringify(spec);
      if (this.lastKey !== key) this.restore();
      this.lastKey = key;
      try {
        const result = await quote(spec);
        if (!this.isConnected || this.revision !== revision) return;
        this.restore();
        if (!result.applied.size || result.total >= spec.base * quantity) {
          this.badges('unavailable'); return;
        }
        const exactUnit = result.total / quantity;
        const unit = Math.round(exactUnit);
        const compare = Math.max(spec.base, Number(this.dataset.comparePrice) || 0);
        this.querySelectorAll('[data-hh-price-value]').forEach(node => {
          node.textContent = `${Number.isInteger(exactUnit) ? '' : '≈ '}${money(unit, spec.currency)}`;
          node.classList.add('sale');
        });
        this.querySelectorAll('[data-hh-price-compare]').forEach(node => {
          const value = node.querySelector('[data-hh-price-compare-value]') || node;
          value.textContent = money(compare, spec.currency);
          node.hidden = false;
        });
        this.querySelectorAll('[data-hh-unit-value]').forEach(node => {
          const ratio = Number(node.dataset.hhUnitRatio);
          if (Number.isFinite(ratio) && ratio > 0) node.textContent = money(Math.round(exactUnit * ratio), spec.currency);
        });
        this.querySelectorAll('[data-hh-serving-value]').forEach(node => {
          const servings = Number(node.dataset.hhServings);
          if (Number.isFinite(servings) && servings > 0) node.textContent = money(Math.round(exactUnit / servings), spec.currency);
        });
        this.dataset.discountedPrice = String(unit);
        this.dataset.discountedLinePrice = String(result.total);
        this.badges('unavailable', result.applied);
        this.dataset.discountPriceState = 'applied';
      } catch (_) {
        if (!this.isConnected || this.revision !== revision) return;
        this.restore(); this.badges('unverified');
      }
    }
  }
  customElements.define('hh-discount-price', HHDiscountPrice);
  window.addEventListener('hh:discount:state', () => instances.forEach(element => element.refresh()));
  const quantityChanged = event => {
    if (!event.target.matches?.(quantitySelector)) return;
    const section = event.target.closest('.shopify-section');
    // hh-pdp-quantity clamps/rounds the value in its target change handler.
    // Read the committed value after that handler, including non-bubbling changes.
    queueMicrotask(() => instances.forEach(element => {
      if (element.dataset.scope === 'product' && element.closest('.shopify-section') === section) element.refresh();
    }));
  };
  document.addEventListener('change', quantityChanged, true);
  document.addEventListener('input', quantityChanged, true);
})();
